import React, { useState, useRef, useEffect } from "react";
import { twMerge } from "tailwind-merge";
import { FiMinus } from "react-icons/fi";
import dropDownUpArrow from "../../assets/icons/dropDownUpArrow.png";
import dropDownDownArrow from "../../assets/icons/dropDownDownArrow.png";
import { FormHelperText, formStyles } from "./FormUtils";

const Dropdown = ({
  options = [],
  placeholder = "Select an option",
  defaultOption = null,
  displayKey = "name",
  idKey = "_id",
  isSearchable = false,
  disabled = false,
  wrapperClassName = "",
  buttonClassName = "",
  dropdownClassName = "",
  className = "",
  optionClassName = "",
  filterClassName = "",
  outerContainerStyle = "",
  selectedValueClassName = "",
  selectedButtonClassName = "",
  placeholderClassName = "",
  truncateDisplay = false,
  maxDisplayLength = 20,
  onSelect,
  error,
  label,
  type,
  labelStyle,
  isRequired = false,
  name,
  onBlur,
  onFocus,
  allowFillColor = false,
  allowClear = false,
  dropdownTyping = false,
  errorMessage,
  errorContainerStyle = "",
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const [searchTerm, setSearchTerm] = useState("");
  const [selectedOption, setSelectedOption] = useState(defaultOption);
  const [focusedIndex, setFocusedIndex] = useState(-1);
  const dropdownRef = useRef(null);
  const optionRefs = useRef([]);
  const buttonRef = useRef(null);
  const searchInputRef = useRef(null);

  useEffect(() => {
    const handleClickOutside = (e) => {
      if (dropdownRef.current && !dropdownRef.current.contains(e.target)) {
        setIsOpen(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  useEffect(() => {
    if (
      defaultOption &&
      (!selectedOption || selectedOption[idKey] !== defaultOption[idKey])
    ) {
      setSelectedOption(defaultOption);

      if (!isOpen) {
        setSearchTerm(getDisplayValue(defaultOption));
      }
      onSelect && onSelect(defaultOption);
    }
  }, [defaultOption, isOpen]);

  useEffect(() => {
    if (focusedIndex >= 0 && optionRefs.current[focusedIndex]) {
      optionRefs.current[focusedIndex].scrollIntoView({
        behavior: "smooth",
        block: "nearest",
      });
    }
  }, [focusedIndex]);

  const dataOptions =
    type === "year"
      ? Array.from({ length: 30 }, (_, i) => {
          const year = (new Date().getFullYear() - i).toString();
          return { _id: year, name: year };
        })
      : options;

  const getDisplayValue = (option) => {
    if (!option) return "";
    if (displayKey.includes("+")) {
      return displayKey
        .split("+")
        .map((key) => option[key.trim()])
        .join(" ");
    }
    return option[displayKey] || "";
  };

  const isTypedValueExist = dataOptions.some(
    (opt) =>
      getDisplayValue(opt).toLowerCase() === searchTerm.trim().toLowerCase()
  );

  const filteredOptions = dataOptions.filter((option) =>
    getDisplayValue(option).toLowerCase().includes(searchTerm.toLowerCase())
  );

  const handleSelect = (option) => {
    setSelectedOption(option);
    setSearchTerm("");
    setIsOpen(false);
    setFocusedIndex(-1);
    onSelect && onSelect(option);
    setTimeout(() => {
      if (buttonRef.current) {
        buttonRef.current.blur(); // force blur
        buttonRef.current.focus(); // immediately restore focus
      }
    }, 0);
    if (name && onBlur) onBlur(name, getDisplayValue(option));
  };

  const handleAddTypedOption = () => {
    const custom = { [idKey]: null, [displayKey]: searchTerm, isCustom: true };
    handleSelect(custom);
  };

  const handleKeyDown = (e) => {
    const key = e.key;

    // Open dropdown on Enter or Space if closed
    if (!isOpen && (key === "Enter" || key === " ")) {
      e.preventDefault();
      setIsOpen(true);
      setFocusedIndex(0);
      return;
    }

    if (!isOpen) return; // Do nothing if closed and it's not a toggle key

    switch (key) {
      case "ArrowDown":
        e.preventDefault();
        setFocusedIndex((prev) =>
          prev < filteredOptions.length - 1 ? prev + 1 : 0
        );
        break;

      case "ArrowUp":
        e.preventDefault();
        setFocusedIndex((prev) =>
          prev > 0 ? prev - 1 : filteredOptions.length - 1
        );
        break;

      case "Enter":
        e.preventDefault();
        if (focusedIndex >= 0 && focusedIndex < filteredOptions.length) {
          handleSelect(filteredOptions[focusedIndex]);
        } else if (searchTerm.trim() && dropdownTyping && !isTypedValueExist) {
          handleAddTypedOption();
        }
        break;

      case "Escape":
        e.preventDefault();
        setIsOpen(false);
        setTimeout(() => {
          if (buttonRef.current) buttonRef.current.focus();
        }, 0);
        break;

      default:
        break;
    }
  };

  const handleClear = () => {
    setSelectedOption(null);
    setSearchTerm("");
    onSelect && onSelect(null);
  };

  return (
    <div className={twMerge("flex flex-col  w-full", outerContainerStyle)}>
      {label && (
        <div className="flex items-center gap-2">
          <label
            className={twMerge(
              "text-light-gray font-inter font-medium text-sm ",
              formStyles.dropDownlabel,
              labelStyle
            )}
          >
            {label} {isRequired && <span className="text-light-red">*</span>}
          </label>
          {errorMessage && (
            <span className="text-[#C00707] text-[12px] leading-[22px] font-['Public_Sans'] font-normal ml-2 whitespace-nowrap mb-2">
              {errorMessage}
            </span>
          )}
        </div>
      )}

      <div ref={dropdownRef} className={twMerge("relative", wrapperClassName)}>
        <button
          ref={buttonRef}
          type="button"
          onClick={() => {
            setIsOpen((prev) => !prev);
            setSearchTerm("");
            setFocusedIndex(0);
            onFocus && onFocus();
          }}
          onKeyDown={(e) => {
            if (e.key === "Tab" && !e.shiftKey && isOpen && isSearchable) {
              e.preventDefault();
              setTimeout(() => searchInputRef.current?.focus(), 0);
            } else {
              handleKeyDown(e);
            }
          }}
          className={twMerge(
            "w-full px-4 py-2 pr-10 border rounded-lg bg-[#45444433] text-[#E2E2E2] flex justify-between items-center",
            disabled ? "cursor-not-allowed bg-[#2C2C2C]" : "",
            error ? "border-light-red" : "border border-[#808080]",
            formStyles.dropDown,
            buttonClassName,
            selectedButtonClassName
          )}
        >
          <span
            className={twMerge(
              "truncate text-left w-full",
              selectedOption
                ? "opacity-100 font-medium"
                : "text-[#808080]",
              selectedValueClassName,
              placeholderClassName
            )}
          >
            {selectedOption
              ? getDisplayValue(selectedOption)
              : placeholder || "Select an option"}
          </span>
          <img
            src={isOpen ? dropDownDownArrow : dropDownUpArrow}
            alt="arrow"
            className="w-[12px] h-[8px] pointer-events-none"
          />
        </button>

        {isOpen && (
          <div
            className={twMerge(
              "absolute w-full z-10 rounded-b-lg bg-[#111111D6] border border-[#505050]",
              dropdownClassName
            )}
            role="listbox"
            tabIndex={-1}
          >
            {isSearchable && (
              <input
                ref={searchInputRef}
                type="text"
                placeholder="Search..."
                className="w-full px-2 py-2 text-[#808080] rounded bg-[#454444]"
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === "Escape") {
                    e.preventDefault();
                    setIsOpen(false);
                    setTimeout(() => buttonRef.current?.focus(), 0);
                  } else if (e.key === "Tab" && !e.shiftKey) {
                    e.preventDefault();
                    setTimeout(() => buttonRef.current?.focus(), 0);
                  } else {
                    handleKeyDown(e);
                  }
                }}
              />
            )}

            <div
              tabIndex={0}
              className={twMerge(
                "max-h-[190px] overflow-y-auto scrollbar-no-radius",
                filterClassName
              )}
            >
              {filteredOptions.map((option, index) => (
                <div
                  key={option[idKey]}
                  ref={(el) => (optionRefs.current[index] = el)}
                  onClick={() => handleSelect(option)}
                  role="option"
                  aria-selected={
                    selectedOption && option[idKey] === selectedOption[idKey]
                  }
                  className={twMerge(
                    "px-4 py-2 cursor-pointer border border-[#808080]",
                    optionClassName,
                    focusedIndex === index
                      ? "bg-[#565656] text-white font-semibold"
                      : "hover:bg-[#080B12]"
                  )}
                >
                  <span className="truncate text-[#E3E3E3]">
                    {getDisplayValue(option)}
                  </span>
                </div>
              ))}

              {filteredOptions.length === 0 && (
                <div className="px-4 py-2 text-gray-500 font-inter font-medium text-center">
                  No results found
                </div>
              )}
            </div>
          </div>
        )}

        {selectedOption && !disabled && allowClear !== false && (
          <div
            onClick={handleClear}
            className="absolute top-[10px] right-[36px] w-4 h-4 rounded-full bg-red-100 flex items-center justify-center hover:bg-red-200 cursor-pointer"
          >
            <FiMinus className="w-3 h-3 text-red-500" />
          </div>
        )}

        {error && (
          <FormHelperText
            error={error}
            className={errorContainerStyle}
            errorClassName={formStyles.errorText}
          />
        )}
      </div>
    </div>
  );
};

export default Dropdown;
